/**
 * A Team's people, as the page says them: the faces in the queue header, the
 * "On this Team" list behind them, Team settings' People, and the ticket
 * sheet's line to a reader who is not on the Team.
 *
 * Membership is what lets a person act (start plans, approve, answer); seeing
 * the Team only lets them read and comment. Who may change the roster is the
 * server's `guardMemberWrite`: a workspace admin or the Team's owner, which is
 * its publisher.
 */
import type { Job } from "@yagni/shared";

import { deskAccentToken } from "../../desks/deskAccent.js";
import { firstNameOf } from "../../lib/names.js";
import { questionAbout } from "../copy.js";
import type { TeamAnswerers, TeamMember } from "../teamsApi.js";
import { SOURCE_SHAPE_COPY, type SourceShape } from "./sourceShape.js";

/** The most faces the header stack shows before "+N". */
export const STACK_MAX = 3;

export const PEOPLE_RULE =
  "Members start plans, approve and answer. Everyone else who can see this Team reads and comments.";
export const PEOPLE_SETTINGS_HELP =
  "Members start plans, approve and answer. Removing someone keeps everything they did on the record.";
export const SEEN_BY_HELP =
  "Seeing a Team lets someone read and comment. Acting on it takes being one of its People.";
export const ASKS_HELP =
  "Reeve and Wright route their questions to these people by topic. Unrouted questions come to you. Only the Team's People can be asked.";
export const ADD_PEOPLE_CAN =
  "They can start plans, approve and answer on this Team, and it shows in their sidebar.";

/** What the page calls a person: their name, else their email, else "Someone". */
export function personName(member: Pick<TeamMember, "displayName" | "email">): string {
  return member.displayName?.trim() || member.email?.trim() || "Someone";
}

export function personFirstName(member: Pick<TeamMember, "displayName" | "email">): string {
  return firstNameOf(member.displayName) ?? member.email?.split("@")[0] ?? "Someone";
}

/** A face with no photo: the same tint for the same person, everywhere, in both schemes. */
export function faceTint(userId: string): { background: string; color: string } {
  const tone = deskAccentToken(userId);
  return { background: `color-mix(in srgb, ${tone} 18%, var(--paper-bright))`, color: tone };
}

/** The lines a person signs, by the stage word the page shows. */
const SIGNED_LINES: ReadonlyArray<readonly [string, string]> = [
  ["pm.propose", "Propose"],
  ["staff.plan", "Plan"],
  ["builder.build", "Build"],
  ["reviewer.review_pr", "Review"],
];

/** "Propose and Plan", "Propose, Plan and Build". */
export function andList(words: readonly string[]): string {
  if (words.length <= 1) return words[0] ?? "";
  return `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
}

/**
 * What a person answers on this Team, under their name: the supervised lines
 * addressed to them ("Answers Propose and Plan"), else the questions routed to
 * them ("Answers questions about product"), else just "Member".
 */
export function answersLine(
  userId: string,
  jobs: readonly Job[],
  answerers: TeamAnswerers,
): string {
  const stages = SIGNED_LINES.filter(([kind]) =>
    jobs.some(
      (job) =>
        job.kind === kind &&
        job.active &&
        job.authority !== "autonomous" &&
        job.supervisorUserId === userId,
    ),
  ).map(([, word]) => word);
  if (stages.length > 0) return `Answers ${andList(stages)}`;
  const kinds = Object.entries(answerers)
    .filter(([, ids]) => Array.isArray(ids) && ids.includes(userId))
    .map(([kind]) => questionAbout(kind));
  if (kinds.length > 0) return `Answers questions about ${andList(kinds)}`;
  return "Member";
}

/** "Maya", "Maya and Jack", "Maya, Jack and Sam", "Maya, Jack and 2 others". */
export function namesPhrase(names: readonly string[], max = 2): string {
  if (names.length === 0) return "Its members";
  if (names.length <= max + 1) return andList(names);
  const rest = names.length - max;
  return `${names.slice(0, max).join(", ")} and ${rest} ${rest === 1 ? "other" : "others"}`;
}

/** Who may add and remove people: a workspace admin or the Team's owner (its publisher). */
export function canManagePeople(input: {
  isAdmin: boolean;
  viewerId: string | null;
  publisherUserId: string | null | undefined;
}): boolean {
  if (input.isAdmin) return true;
  return input.viewerId !== null && input.viewerId === (input.publisherUserId ?? null);
}

/** The owner first, then everyone else in the order they joined (the server's order). */
export function ownerFirst(members: readonly TeamMember[]): TeamMember[] {
  return [...members].sort((a, b) => Number(b.role === "owner") - Number(a.role === "owner"));
}

/** "Jack's Team": the name a personal Team is offered when it is shared. */
export function sharedTeamName(owner: Pick<TeamMember, "displayName" | "email"> | null): string {
  return owner ? `${personFirstName(owner)}'s Team` : "Our Team";
}

/**
 * The comment that asks a Team's owner to add the reader: an @mention when the
 * owner has a handle (the comments route notifies them), else their name.
 */
export function askToJoinComment(input: {
  owner: Pick<TeamMember, "displayName" | "email" | "handle"> | null;
  teamName: string;
  memberVerb: string;
}): string {
  const team = input.teamName.trim() || "this Team";
  const ask = `please add me to ${team} so I can ${input.memberVerb}.`;
  if (!input.owner) return `Could someone ${ask}`;
  if (input.owner.handle?.trim())
    return `@${input.owner.handle.trim()} ${ask[0]!.toUpperCase()}${ask.slice(1)}`;
  return `${personFirstName(input.owner)}, ${ask}`;
}

/**
 * The comment that asks a Team's owner to add a connected tool a plan question
 * needs, said the way `askToJoinComment` says its ask: an @mention when the
 * owner has a handle, else their name, else anyone.
 */
export function askToAddSourceComment(input: {
  owner: Pick<TeamMember, "displayName" | "email" | "handle"> | null;
  teamName: string;
  source: string;
  askerName: string;
  /**
   * Every workspace connection reaches a Team by default (YAG-986): a source
   * the Team lacks is either hidden from it (`show`) or not connected in the
   * workspace at all (`connect`). Older callers say "add".
   */
  mode?: SourceShape;
}): string {
  const team = input.teamName.trim() || "this Team";
  const request = SOURCE_SHAPE_COPY[input.mode ?? "add"].request(input.source, team);
  const ask = `${request} so ${input.askerName} can check the plan's open question.`;
  if (!input.owner) return `Could someone ${ask}`;
  if (input.owner.handle?.trim())
    return `@${input.owner.handle.trim()} ${ask[0]!.toUpperCase()}${ask.slice(1)}`;
  return `${personFirstName(input.owner)}, ${ask}`;
}
